/**
 * Forces muted looping videos to autoplay in Safari, including iOS.
 *
 * Safari will not autoplay unless the element is muted + playsinline, and it
 * often ignores the HTML autoplay attribute until `play()` is called. HLS
 * sources from Shopify's video_tag are also removed so Safari uses MP4.
 */
class AutoplayVideo extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    this.#video = video;
    this.#preferMp4(video);
    this.#prepare(video);
    this.#tryPlay();

    const { signal } = this.#abort;

    video.addEventListener('canplay', this.#tryPlay, { signal });
    video.addEventListener('loadeddata', this.#tryPlay, { signal });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') this.#tryPlay();
      },
      { signal }
    );
    window.addEventListener('pageshow', this.#tryPlay, { signal });

    for (const eventName of ['pointerdown', 'touchstart', 'keydown']) {
      document.addEventListener(eventName, this.#tryPlay, { signal, passive: true });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#tryPlay();
          }
        },
        { threshold: 0 }
      );
      observer.observe(video);
      signal.addEventListener('abort', () => observer.disconnect());
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /**
   * Safari prefers HLS when Shopify outputs an m3u8 source, and HLS autoplay
   * is less reliable than MP4 for muted background videos.
   * @param {HTMLVideoElement} video
   */
  #preferMp4(video) {
    let removedHls = false;

    for (const source of video.querySelectorAll('source')) {
      const type = (source.getAttribute('type') || '').toLowerCase();
      const src = source.getAttribute('src') || '';

      if (type.includes('mpegurl') || src.includes('.m3u8')) {
        source.remove();
        removedHls = true;
      }
    }

    if (removedHls) video.load();
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #prepare(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.removeAttribute('controls');
  }

  #tryPlay = () => {
    const video = this.#video;
    if (!video || !video.paused) return;

    this.#prepare(video);

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay was blocked (Low Power Mode, Safari autoplay setting).
        // Gesture and visibility listeners retry until it succeeds.
      });
    }
  };
}

if (!customElements.get('autoplay-video')) {
  customElements.define('autoplay-video', AutoplayVideo);
}
